import { Stack } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";

import PrimaryButton from "../../../../../../common/components/primary_button";
import { BiometriaFacial } from "../../../../domain/models/biometria_facial";
import { GenerateBiometriaFacialColumnsDefinition } from "../columns/generate_biometria_facial";

type Props = {
  loading: boolean;
  biometriaFacial: BiometriaFacial[];
  biometriaFacialPage: number;
  biometriaFacialRowsPerPage: number;
  biometriaFacialTotalCount: number;
  onBiometriaFacialPageChange: (page: number) => void;
  onBiometriaFacialRowsPerPageChange: (rowsPerPage: number) => void;
  onCadastrarBiometriaFacial: (
    idBiometriaFacial: number | null,
    openState: boolean,
  ) => void;
  onDeletarBiometriaFacial: (
    idBiometriaFacial: number | null,
    openState: boolean,
  ) => void;
  onActionVisualize: (idBiometriaFacial: number) => void;
  onActionAuditoria: (auditId: string) => void;
};

export const TabBiometriaFacialContent = ({
  loading,
  biometriaFacial,
  biometriaFacialPage,
  biometriaFacialRowsPerPage,
  biometriaFacialTotalCount,
  onBiometriaFacialPageChange,
  onBiometriaFacialRowsPerPageChange,
  onCadastrarBiometriaFacial,
  onDeletarBiometriaFacial,
  onActionVisualize,
  onActionAuditoria,
}: Props) => {
  // Estado para armazenar o tempo restante do cooldown (em segundos)
  const [cooldownTime, setCooldownTime] = useState<any>(0);

  // Efeito que gerencia o cronômetro lendo do LocalStorage
  useEffect(() => {
    const calculateCooldown = () => {
      const deleteTime = localStorage.getItem("biometria_delete_time");
      if (deleteTime) {
        // Calcula a diferença entre agora e a hora que deletou (em segundos)
        const diffSeconds = Math.floor(
          (Date.now() - parseInt(deleteTime, 10)) / 1000,
        );
        const remaining = 300 - diffSeconds; // 300 segundos = 5 minutos

        if (remaining > 0) {
          setCooldownTime(remaining);
        } else {
          setCooldownTime(0);
          localStorage.removeItem("biometria_delete_time");
        }
      }
    };

    calculateCooldown();
    const interval = setInterval(calculateCooldown, 1000);

    return () => clearInterval(interval);
  }, []);

  const columnsDefinition = useMemo(() => {
    return GenerateBiometriaFacialColumnsDefinition({
      onDeletar: (id) => {
        localStorage.setItem("biometria_delete_time", Date.now().toString());
        setCooldownTime(300);
        onDeletarBiometriaFacial(id, true);
      },
      onActionVisualize: (idBiometriaFacial) =>
        onActionVisualize(idBiometriaFacial),
      onActionAuditoria: (auditId) => onActionAuditoria(auditId),
    });
  }, [onActionAuditoria, onActionVisualize, onDeletarBiometriaFacial]);

  const biometriaAtiva = biometriaFacial.find((b) => b.Ativo === true);

  const possuiBiometriaAtiva = !!biometriaAtiva;
  const precisaCadastrarBiometria = !possuiBiometriaAtiva;

  const idTermoDaBiometria = biometriaAtiva?.IdTermoEPI;
  const mostrarBotaoTermo = possuiBiometriaAtiva && !idTermoDaBiometria;

  const minutes = Math.floor(cooldownTime / 60);
  const seconds = cooldownTime % 60;
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <>
      <>
        <div className="div flex mt-8 justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <div className="flex gap-2 items-center w-full">
            <h1 className="text-xl font-medium text-black dark:text-white">
              Biometria Facial
            </h1>
          </div>

          <div className="flex justify-end gap-2">
            {precisaCadastrarBiometria && (
              <PrimaryButton
                className="whitespace-nowrap"
                sx={{
                  paddingX: 3,
                  ...(cooldownTime > 0 && {
                    "&.Mui-disabled": {
                      backgroundColor: "#ef4444 !important",
                      color: "#ffffff !important",
                    },
                  }),
                }}
                disabled={cooldownTime > 0}
                onClick={() => onCadastrarBiometriaFacial(null, true)}
              >
                {cooldownTime > 0
                  ? `Aguarde ${formattedTime} para cadastrar`
                  : "Cadastrar Biometria Facial"}
              </PrimaryButton>
            )}

          </div>
        </div>
      </>

      <Stack spacing={0} className="mt-4">
        <StyledDataGrid
          tableName="tab_biometria_facial_content"
          disableColumnMenu={false}
          disableRowSelectionOnClick={true}
          getRowId={(row: BiometriaFacial) => row.IdBiometriaFacial}
          columns={columnsDefinition}
          loading={loading}
          rows={biometriaFacial}
          rowCount={biometriaFacial.length}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
          paginationModel={{
            page: biometriaFacialPage,
            pageSize: biometriaFacialRowsPerPage,
          }}
        />
      </Stack>
    </>
  );
};
